import React from 'react';

import {
  ComponentDesign,
  formatLumberGrade,
  formatLumberSpecies,
  GroupedProductionComponentInstance,
  Hanger,
  Member,
  PlatePair,
} from '@paragon/contract-library';
import { DataRow, Row } from '@paragon/layout-models';
import { getPlateTypeAndSizeString, useNominalLumberDimensionFormatter } from '@paragon/material-lists';
import { assertExhaustiveSwitch, formatAsNaturalLanguageList, naturalSort } from '@paragon/utilities';

import { getComponentDesignAnalysisStatus } from '../../../../../store/selectors/componentDesignAnalysisStatusSelectors';
import { getComponentDesignGuidToQuantity } from '../../../../../store/selectors/componentDesignQuantitySelectors';
import { createDoWorkingChangesExistOnComponentDesign } from '../../../../../store/selectors/workingChangesSelectors';
import { useAppSelector } from '../../../../hooks';

export function SecondaryRowText(props: { row: Row }) {
  const { row } = props;

  const jackGirderConnections = useAppSelector((state) => state.jackGirderConnections.data);
  const componentDesigns = useAppSelector((state) => state.componentDesigns.data);

  const formatLumberDimensions = useNominalLumberDimensionFormatter();

  switch (row.type) {
    case 'platePair': {
      const dataRow = row as DataRow<PlatePair>;

      return getPlateTypeAndSizeString(dataRow.item);
    }

    case 'member':
    case 'brace': {
      const { lumber } = (row as DataRow<Member>).item;

      const information = [
        formatLumberGrade(lumber.grade, lumber.tangibleGrades),
        formatLumberSpecies(lumber.species, lumber.tangibleSpecies),
        formatLumberDimensions(lumber),
      ];

      return information.filter((i) => i != null).join(' ');
    }

    case 'hanger': {
      const hanger = (row as DataRow<Hanger>).item;

      const associatedJackGuids = Object.values(jackGirderConnections)
        .filter(({ hangerGuid }) => hangerGuid === hanger.guid)
        .map(({ jackGuid }) => jackGuid);

      const associatedJackNames = Object.values(componentDesigns)
        .filter(({ guid }) => associatedJackGuids.includes(guid))
        .map(({ component }) => component.name)
        .toSorted(naturalSort);

      return formatAsNaturalLanguageList(associatedJackNames, '&');
    }

    case 'profileTrussDesign':
    case 'featurePatternTrussDesign': {
      return <ComponentDesignSecondaryText design={(row as DataRow<ComponentDesign>).item} />;
    }

    case 'groupedProductionComponentInstance': {
      return (
        <GroupedProductionComponentInstanceSecondaryText
          item={(row as DataRow<GroupedProductionComponentInstance>).item}
        />
      );
    }

    default:
      return null;
  }
}

function ComponentDesignSecondaryText({ design }: { design: ComponentDesign }) {
  const doWorkingChangesExistOnComponentDesign = createDoWorkingChangesExistOnComponentDesign(design.guid);
  const openContext = useAppSelector((state) => state.application.openContext?.type);
  const activeComponentDesignGuid = useAppSelector((state) => state.selections.activeComponentDesignGuid);
  const componentDesignQuantities = useAppSelector(getComponentDesignGuidToQuantity);
  const hasWorkingChanges = useAppSelector(doWorkingChangesExistOnComponentDesign);
  const timestamps = useAppSelector((state) => state.componentDesignTimestamps[design.guid]);

  if (openContext === 'station')
    return design.guid !== activeComponentDesignGuid && componentDesignQuantities[design.guid]
      ? `Quantity: ${componentDesignQuantities[design.guid]}`
      : null;

  const analysisStatus = getComponentDesignAnalysisStatus(timestamps, hasWorkingChanges);
  switch (analysisStatus) {
    case 'unanalyzed':
      return 'Unanalyzed';
    case 'checkExceeded':
      return 'Check Exceeded';
    case 'hasErrors':
      return 'Has errors';
    case 'passing':
      return 'Checks Pass';
    case null:
      return null;
    default:
      assertExhaustiveSwitch(analysisStatus);
  }
}

function GroupedProductionComponentInstanceSecondaryText(props: { item: GroupedProductionComponentInstance }) {
  const { item } = props;
  return `Bundle${item.bundleIds && item.bundleIds.length > 1 ? 's' : ''} ${item.bundleIds}`;
}
